import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type {
  Block,
  Inline,
  Text as ContentfulText,
} from '@contentful/rich-text-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { ReactNode } from 'react';
import React from 'react';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import * as M from '@/components/Cms/Card/components/BaseCardType/RichTextEditor/CardRichTextRenderer.styles';
import {
  RENDER_TEXT_OPTION,
  renderMarkOption,
  renderTableRow,
  useTableData,
} from '@/components/Cms/Card/components/BaseCardType/RichTextEditor/CardRichTextShared';
import { List } from '@/components/List';
import { Typography } from '@/components/Typography';
import type { ContentfulFreeTextContentFragment } from '@/graphql/contentful/api';
import { useContactInfo } from '@/hooks/useContactInfo';
import { useRegionProvider } from '@/providers/RegionProvider';

import ButtonWithTranslation from './ButtonWithTranslation';

import * as S from './FreeTextContent.styles';

export type FreeTextContentLinksType = {
  __typename?: 'FreeTextContentTextContentLinks';
  assets: {
    __typename?: 'FreeTextContentTextContentAssets';
    block: Array<{
      __typename?: 'Asset';
      sys: { __typename?: 'Sys'; id: string };
      url?: string | null;
    } | null>;
  };
};

export const InsertDynamicaValuesIntoTextWrapper = ({
  children,
  node,
}: {
  children: ReactNode;
  node: Block | Inline;
}) => {
  const contactInfo = useContactInfo();

  return (
    <InsertDynamicValuesIntoText contactInfo={contactInfo} node={node}>
      {children}
    </InsertDynamicValuesIntoText>
  );
};

const InsertDynamicValuesIntoText = ({
  contactInfo,
  node,
}: {
  children: ReactNode;
  contactInfo: {
    email: string;
    phoneNumber: string;
  };
  node: Block | Inline;
}) => {
  const combinedTextElements: React.ReactNode[] = [];

  node.content?.forEach((child, index) => {
    if ('value' in child && (child as ContentfulText).value) {
      const textValue = (child as ContentfulText).value;

      const parts = textValue.split(
        /(\{\{InsertCookieSettings\}\}|%{{contact.phone}}%|%{{contact.email}}%)/g,
      );

      parts.forEach((part, i) => {
        if (part === '%{{contact.phone}}%') {
          combinedTextElements.push(
            <a
              href={`tel:${contactInfo.phoneNumber}`}
              style={{ display: 'inline' }}
            >
              <strong>{contactInfo.phoneNumber}</strong>
            </a>,
          );
        } else if (part === '%{{contact.email}}%') {
          combinedTextElements.push(
            <a
              href={`mailto:${contactInfo.email}`}
              style={{ display: 'inline' }}
            >
              <strong>{contactInfo.email}</strong>
            </a>,
          );
        } else if (part === '{{InsertCookieSettings}}') {
          combinedTextElements.push(
            <ButtonWithTranslation key={`button-${index}-${i}`} />,
          );
        } else if (/https?:\/\/[^\s]+/.test(part)) {
          combinedTextElements.push(
            <a
              key={`link-${index}-${i}`}
              href={part}
              rel='noopener noreferrer'
              target='_blank'
            >
              {part}
            </a>,
          );
        } else {
          combinedTextElements.push(part);
        }
      });
    }
  });

  return <Typography scale='p2'>{combinedTextElements}</Typography>;
};

const createRenderNodeOption = (languageCode: string) => ({
  [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
    const { uri } = node.data;
    const isExternalLink =
      uri.startsWith('http://') || uri.startsWith('https://');
    return (
      <M.StyledLink
        aria-label={getLinkAriaLabel(uri, languageCode)}
        href={uri}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        target={isExternalLink ? '_blank' : '_self'}
      >
        {children}
      </M.StyledLink>
    );
  },
  [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => (
    <List>{children}</List>
  ),
  [BLOCKS.OL_LIST]: (node: Block | Inline, children: ReactNode) => {
    return <M.StyledOl>{children}</M.StyledOl>;
  },
  [BLOCKS.TABLE]: (node: Block | Inline, children: ReactNode) => {
    return (
      <M.StyledTable>
        <tbody>{children}</tbody>
      </M.StyledTable>
    );
  },

  [BLOCKS.TABLE_CELL]: (node: Block | Inline, children: ReactNode) => {
    return <M.StyledCell>{children}</M.StyledCell>;
  },

  [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
    const isInsertCookiePolicy = node.content?.some(
      (child) =>
        'value' in child &&
        (child as unknown as ContentfulText).value?.trim() ===
          '{{InsertCookiePolicy}}',
    );

    if (isInsertCookiePolicy) {
      return <div id='ot-sdk-cookie-policy'></div>;
    }

    const textValue = node.content
      ?.map((child) =>
        'value' in child ? (child as unknown as ContentfulText).value : '',
      )
      .join('');

    if (
      textValue?.includes('{{InsertCookieSettings}}') ||
      textValue?.includes('%{{contact.phone}}%') ||
      textValue?.includes('%{{contact.email}}%')
    ) {
      return (
        <S.StyledParagraph>
          <InsertDynamicaValuesIntoTextWrapper node={node}>
            {children}
          </InsertDynamicaValuesIntoTextWrapper>
        </S.StyledParagraph>
      );
    }

    return <S.StyledParagraph scale='p2'>{children}</S.StyledParagraph>;
  },
  [BLOCKS.HEADING_1]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.SubTitle fontWeight='extraLight' scale='h1'>
        {children}
      </S.SubTitle>
    );
  },
  [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.SubTitle fontWeight='extraLight' scale='h2'>
        {children}
      </S.SubTitle>
    );
  },
  [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.SubTitle fontWeight='extraLight' scale='h3'>
        {children}
      </S.SubTitle>
    );
  },
  [BLOCKS.HEADING_4]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.SubTitle fontWeight='extraLight' scale='h4'>
        {children}
      </S.SubTitle>
    );
  },
  [BLOCKS.HEADING_5]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.SubTitle fontWeight='extraLight' scale='h5'>
        {children}
      </S.SubTitle>
    );
  },
  [BLOCKS.HEADING_6]: (node: Block | Inline, children: ReactNode) => {
    return (
      <S.SubTitle fontWeight='extraLight' scale='h6'>
        {children}
      </S.SubTitle>
    );
  },
  [BLOCKS.QUOTE]: (node: Block | Inline, children: ReactNode) => (
    <M.StyledQuote>{children}</M.StyledQuote>
  ),
  [BLOCKS.HR]: () => <hr />,
});
const renderOptions = (
  links: FreeTextContentLinksType,
  data: string[],
  languageCode: string,
): Options => {
  const assetBlockMap = new Map();
  if (links?.assets?.block) {
    for (const asset of links.assets.block) {
      if (asset?.sys?.id) {
        assetBlockMap.set(asset.sys.id, asset);
      }
    }
  }
  return {
    renderNode: {
      ...createRenderNodeOption(languageCode),
      [BLOCKS.EMBEDDED_ASSET]: (node: Block | Inline) => {
        const asset = assetBlockMap.get(node.data.target.sys.id);
        if (asset?.url)
          return (
            <>
              <S.StyledImageMobile
                alt='embeddedImage'
                aspectRatio={{ x: 0, y: 0 }}
                focalPoint={{ x: 0, y: 0 }}
                loaderProps={{ height: 245 }}
                loading='lazy'
                originalImageDimensions={undefined}
                sizes='(max-width: 600px) 50vw, 20vw'
                src={asset.url}
                useLoader
                width='100%'
              />
              <S.ImageWrapperDeskTop>
                <S.StyledImageDeskTop
                  alt='embeddedImage'
                  aspectRatio={{ x: 0, y: 0 }}
                  focalPoint={{ x: 0, y: 0 }}
                  loaderProps={{ height: 245 }}
                  loading='lazy'
                  originalImageDimensions={undefined}
                  sizes='(max-width: 600px) 50vw, 20vw'
                  src={asset.url}
                  useLoader
                  width='48%'
                />
              </S.ImageWrapperDeskTop>
            </>
          );
      },
      [BLOCKS.TABLE_ROW]: (node: Block | Inline, children: ReactNode) =>
        renderTableRow(node, children, data),
    },
    renderMark: renderMarkOption,
    renderText: RENDER_TEXT_OPTION,
  };
};
export const FreeTextContent = (props: ContentfulFreeTextContentFragment) => {
  const {
    locale: { code: languageCode },
  } = useRegionProvider();
  const data = useTableData(props.freeTextContent?.json.content);
  return (
    <S.FreeTextContentBox>
      {documentToReactComponents(
        props.freeTextContent?.json,
        renderOptions(
          props.freeTextContent?.links as FreeTextContentLinksType,
          data,
          languageCode,
        ),
      )}
    </S.FreeTextContentBox>
  );
};
